import type {
  EmployeeData,
  EmployeeDirectoryEntry,
  EmployeeFormValues,
} from "../schema/employee-schema";
import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";

class EmployeeApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new EmployeeApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  // 204 (delete) has no body.
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new EmployeeApiError(message, res.status);
  }
  return data as T;
}

/**
 * Every employee in the workspace as a slim directory entry (id + name + a few
 * display fields), ordered by name. For pickers and id→name lookups — it never
 * pulls the full record, so it stays fast across the whole workspace. Use
 * {@link fetchEmployeesPage} / {@link getEmployee} when you need full rows.
 */
export async function listEmployeeDirectory(): Promise<EmployeeDirectoryEntry[]> {
  return request<EmployeeDirectoryEntry[]>("/api/employees/directory");
}

/** Sortable columns for the paged employee list. */
export type EmployeeListSort = "name" | "jobTitle" | "department" | "type" | "status";

/** The query that selects one page of employees (server-side). */
export type EmployeeListParams = {
  page: number; // 1-based
  pageSize: number;
  q?: string;
  type?: string;
  status?: string;
  department?: string;
  sort: EmployeeListSort;
  dir: "asc" | "desc";
};

/** One page of employees plus the totals + department list for the UI. */
export type EmployeePage = {
  items: EmployeeData[];
  total: number; // rows matching the active filters
  totalAll: number; // all employees in the workspace
  totalSamples?: number; // how many of `totalAll` are sample employees
  departments: string[];
  page: number;
  pageSize: number;
};

/** Fetch one page of employees with server-side search / filter / sort. */
export async function fetchEmployeesPage(params: EmployeeListParams): Promise<EmployeePage> {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page));
  sp.set("pageSize", String(params.pageSize));
  if (params.q) sp.set("q", params.q);
  if (params.type) sp.set("type", params.type);
  if (params.status) sp.set("status", params.status);
  if (params.department) sp.set("department", params.department);
  sp.set("sort", params.sort);
  sp.set("dir", params.dir);
  return request<EmployeePage>(`/api/employees/page?${sp.toString()}`);
}

/** A single employee by id. */
export async function getEmployee(id: string): Promise<EmployeeData> {
  return request<EmployeeData>(`/api/employees/${id}`);
}

/** Creates a new employee and returns the persisted record. */
export async function createEmployee(data: EmployeeFormValues): Promise<EmployeeData> {
  return request<EmployeeData>("/api/employees", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Updates an existing employee and returns the persisted record. */
export async function updateEmployee(id: string, data: EmployeeFormValues): Promise<EmployeeData> {
  return request<EmployeeData>(`/api/employees/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/** Deletes an employee. */
export async function deleteEmployee(id: string): Promise<void> {
  await request<void>(`/api/employees/${id}`, { method: "DELETE" });
}

/** Removes every sample employee across the account's companies. */
export async function removeSampleEmployees(): Promise<{ removed: number }> {
  return request<{ removed: number }>("/api/employees/samples", { method: "DELETE" });
}

/** The search / filter inputs that define a "select all matching" set. */
export type EmployeeFilterParams = Pick<EmployeeListParams, "q" | "type" | "status" | "department">;

/** Every employee id matching the given filters (for "select all matching"). */
export async function fetchEmployeeIds(filters: EmployeeFilterParams): Promise<string[]> {
  const sp = new URLSearchParams();
  if (filters.q) sp.set("q", filters.q);
  if (filters.type) sp.set("type", filters.type);
  if (filters.status) sp.set("status", filters.status);
  if (filters.department) sp.set("department", filters.department);
  const qs = sp.toString();
  const { ids } = await request<{ ids: string[] }>(`/api/employees/ids${qs ? `?${qs}` : ""}`);
  return ids;
}

/**
 * Resolve a specific set of employees by id (owner-scoped; unknown ids are
 * dropped). Lets the bulk generator hydrate the selection handed off from the
 * employees table without loading the whole workspace.
 */
export async function fetchEmployeesByIds(ids: string[]): Promise<EmployeeData[]> {
  if (ids.length === 0) return [];
  return request<EmployeeData[]>("/api/employees/by-ids", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

/** Bulk-deletes the given employees; returns how many rows were removed. */
export async function bulkDeleteEmployees(ids: string[]): Promise<number> {
  const { deleted } = await request<{ deleted: number }>("/api/employees/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
  return deleted;
}

/** Bulk-sets the employment status on the given employees; returns the count. */
export async function bulkUpdateEmployeeStatus(ids: string[], status: string): Promise<number> {
  const { updated } = await request<{ updated: number }>("/api/employees/bulk-status", {
    method: "POST",
    body: JSON.stringify({ ids, status }),
  });
  return updated;
}

/** The per-employee outcome of a bulk portal invite. */
export type BulkInviteResult = {
  invited: number;
  skipped: number;
  errors: { name: string; message: string }[];
};

/** Most employees a single bulk portal invite accepts (matches the server cap). */
export const MAX_BULK_INVITE = 100;

/**
 * Invites the given employees to the self-service portal. Each invite is
 * independent: already-invited / no-email / email-in-use employees come back as
 * `skipped` with a reason rather than failing the whole batch.
 */
export async function bulkInviteToPortal(ids: string[]): Promise<BulkInviteResult> {
  return request<BulkInviteResult>("/api/employees/bulk-invite", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

/** An employee's portal-access status. */
export type PortalStatus = {
  invited: boolean;
  activated: boolean;
  /** HR turned their access off — the login survives but can't sign in here. */
  revoked: boolean;
  /** Whether HR can re-enable access (true only for a `revoked` link). */
  canReactivate: boolean;
  invitedAt: string | null;
};

/** Whether an employee has been invited to / activated the self-service portal. */
export async function getPortalStatus(id: string): Promise<PortalStatus> {
  return request<PortalStatus>(`/api/employees/${id}/portal`);
}

/**
 * Turn off an employee's portal access. The link is revoked (they're locked out
 * on their next request) and they're emailed a notice; their other portals and
 * the login itself are untouched.
 */
export async function deactivatePortal(id: string): Promise<{ deactivated: boolean }> {
  return request<{ deactivated: boolean }>(`/api/employees/${id}/portal/deactivate`, {
    method: "POST",
  });
}

/**
 * Re-enable a previously-deactivated portal. `reinvited` is true when the person
 * had never set a password and was sent a fresh set-password invite instead of a
 * silent restore.
 */
export async function reactivatePortal(
  id: string,
): Promise<{ reactivated: boolean; reinvited: boolean }> {
  return request<{ reactivated: boolean; reinvited: boolean }>(
    `/api/employees/${id}/portal/reactivate`,
    { method: "POST" },
  );
}

/**
 * Invites an employee to the portal (creates their login + emails a set-password
 * link). Optional `email` overrides the auto-resolved work/personal address.
 */
export async function inviteToPortal(
  id: string,
  email?: string,
): Promise<{ invited: boolean; email: string }> {
  return request<{ invited: boolean; email: string }>(`/api/employees/${id}/invite`, {
    method: "POST",
    body: JSON.stringify(email ? { email } : {}),
  });
}

/** The created/updated/skipped outcome of an import, with per-row errors. */
export type ImportSummary = {
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
};

/** Lifecycle of a queued import batch the worker drains. */
export type ImportBatchStatus = "queued" | "running" | "completed" | "failed" | "cancelled";

/**
 * A bulk-import batch as the API returns it. The worker upserts the rows in the
 * background and updates the counters; the client polls this for live progress.
 */
export type EmployeeImportBatch = {
  id: string;
  fileName: string;
  status: ImportBatchStatus;
  total: number;
  processed: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errors: { row: number; message: string }[];
  createdAt: string;
  updatedAt: string;
};

/** Whether a batch has finished (succeeded or failed) and polling can stop. */
export function isImportTerminal(status: ImportBatchStatus): boolean {
  return status === "completed" || status === "failed" || status === "cancelled";
}

/** Hard cap on import file size, enforced client- and server-side (2 MB). */
export const MAX_IMPORT_BYTES = 2 * 1024 * 1024;

/**
 * The result of uploading + inspecting a spreadsheet: its headers + a row sample
 * for the mapping/preview step, plus the `sourceKey` referencing the file the
 * server stored (the worker re-parses it by this key when the import starts).
 */
export type ImportInspection = {
  sourceKey: string;
  headers: string[];
  sampleRows: string[][];
  total: number;
};

/**
 * Upload a spreadsheet to the API (multipart) and inspect it in one request: the
 * server stores the file and returns its headers + a row sample. The bytes pass
 * through our API (no direct browser → R2 upload), so there's no CORS to set up.
 */
export async function inspectImport(file: File): Promise<ImportInspection> {
  const form = new FormData();
  form.append("file", file);

  let res: Response;
  try {
    res = await apiFetch(apiUrl("/api/employees/import/inspect"), {
      method: "POST",
      credentials: "include",
      // No Content-Type header — the browser sets the multipart boundary.
      body: form,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new EmployeeApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new EmployeeApiError(message, res.status);
  }
  return data as ImportInspection;
}

/**
 * Queue a bulk import of an uploaded spreadsheet. The browser uploads the file to
 * R2 and chooses a column `mapping` (our field key → source column index); the
 * worker parses + upserts the rows. Poll {@link getEmployeeImport} with the
 * returned batch id for progress.
 */
export async function startEmployeeImport(input: {
  sourceKey: string;
  mapping: Record<string, number>;
  total: number;
  fileName: string;
}): Promise<EmployeeImportBatch> {
  return request<EmployeeImportBatch>("/api/employees/import", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Fetch the current state of an import batch (for progress polling). */
export async function getEmployeeImport(batchId: string): Promise<EmployeeImportBatch> {
  return request<EmployeeImportBatch>(`/api/employees/import/${batchId}`);
}

/** Stop a queued/running import. Already-processed rows remain imported. */
export async function cancelEmployeeImport(batchId: string): Promise<EmployeeImportBatch> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    return await request<EmployeeImportBatch>(`/api/employees/import/${batchId}/cancel`, {
      signal: controller.signal,
      method: "POST",
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new EmployeeApiError(
        "The cancel request timed out. Check the import status and try again.",
        0,
      );
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/** The workspace's recent import batches, newest first (upload history). */
export async function listEmployeeImports(): Promise<EmployeeImportBatch[]> {
  const data = await request<{ items: EmployeeImportBatch[] }>("/api/employees/imports");
  return data.items;
}

/**
 * Download a batch's skipped rows as a CSV (their original values plus the error
 * that skipped each one) so the user can fix and re-import them. Fetched as a
 * blob — the file is cross-origin, so the session cookie rides via `credentials`.
 */
export async function downloadImportFailures(batchId: string): Promise<void> {
  const res = await apiFetch(apiUrl(`/api/employees/import/${batchId}/failures`), {
    credentials: "include",
  });
  if (!res.ok) {
    throw new EmployeeApiError(`Couldn't download failed rows (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `import-failed-rows-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * The workspace's most recent still-running import, or null. Used to recover an
 * in-flight import's progress after a page refresh — the batch lives server-side
 * so it keeps advancing even though the client's in-memory state was lost.
 */
export async function getActiveEmployeeImport(): Promise<EmployeeImportBatch | null> {
  const data = await request<{ batch: EmployeeImportBatch | null }>("/api/employees/import/active");
  return data.batch;
}

/** A file attached to an employee profile. */
export type EmployeeAttachment = {
  id: string;
  employeeId: string;
  fileName: string;
  url: string;
  contentType: string;
  sizeBytes: number | null;
  category: string;
  note: string | null;
  createdAt: string;
};

export const ATTACHMENT_CATEGORIES = [
  "contract",
  "id",
  "certificate",
  "medical",
  "signed",
  "other",
] as const;
export type AttachmentCategory = (typeof ATTACHMENT_CATEGORIES)[number];

function normalizeAttachmentUrl(att: EmployeeAttachment): EmployeeAttachment {
  return {
    ...att,
    url: att.url.startsWith("/api/") ? apiUrl(att.url) : att.url,
  };
}

/** All files attached to an employee profile, newest first. */
export async function listAttachments(employeeId: string): Promise<EmployeeAttachment[]> {
  const data = await request<{ items: EmployeeAttachment[] }>(
    `/api/employees/${employeeId}/attachments`,
  );
  return data.items.map(normalizeAttachmentUrl);
}

/** Uploads a file to an employee profile (multipart). */
export async function uploadAttachment(
  employeeId: string,
  file: File,
  meta?: { category?: AttachmentCategory; note?: string },
): Promise<EmployeeAttachment> {
  const form = new FormData();
  form.append("file", file);
  if (meta?.category) form.append("category", meta.category);
  if (meta?.note) form.append("note", meta.note);

  let res: Response;
  try {
    res = await apiFetch(apiUrl(`/api/employees/${employeeId}/attachments/upload`), {
      method: "POST",
      credentials: "include",
      // No Content-Type header — the browser sets the multipart boundary.
      body: form,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new EmployeeApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new EmployeeApiError(message, res.status);
  }
  return normalizeAttachmentUrl((data as { item: EmployeeAttachment }).item);
}

/** Updates an attachment's category/note. */
export async function updateAttachment(
  employeeId: string,
  attachmentId: string,
  patch: { category?: AttachmentCategory; note?: string | null },
): Promise<EmployeeAttachment> {
  const data = await request<{ item: EmployeeAttachment }>(
    `/api/employees/${employeeId}/attachments/${attachmentId}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  return normalizeAttachmentUrl(data.item);
}

/** Removes an attachment (and deletes the stored file). */
export async function deleteAttachment(employeeId: string, attachmentId: string): Promise<void> {
  await request<void>(`/api/employees/${employeeId}/attachments/${attachmentId}`, {
    method: "DELETE",
  });
}

export { EmployeeApiError };
